import { useTryCallApi } from 'common/hooks/use-try-call-api'
import { AssetsSuggestionsService, PresenterSuggestAssetCategoryResponse } from 'common/open-api'
import { useCallback, useEffect, useState } from 'react'

export type File = {
  id: number
  filename: string
  thumbnail_url: string
  url: string
}

export type AIState = {
  assetName?: string
  image?: File
}

/**
 * Manages AI suggestion API state: local input state (assetName, image),
 * submission, response data, and try-again reset.
 */
export const useAISuggestionState = (assetName?: string, image?: File) => {
  const [aiState, setAIState] = useState<AIState>({})
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [responseData, setResponseData] = useState<PresenterSuggestAssetCategoryResponse | undefined>()
  const { data, callApi, isLoading } = useTryCallApi(AssetsSuggestionsService.suggestionsCreate)

  useEffect(() => {
    setAIState(prev => {
      const nextAssetName = prev?.assetName === assetName ? prev.assetName : assetName
      const nextImage = prev?.image === image ? prev.image : image
      if (prev?.assetName === nextAssetName && prev?.image === nextImage) return prev
      return { ...prev, assetName: nextAssetName, image: nextImage }
    })
  }, [assetName, image])

  useEffect(() => {
    if (data) {
      setResponseData(data?.data)
      setHasSubmitted(true)
    }
  }, [data])

  const onSubmit = useCallback(async () => {
    const payload = { asset_name: aiState?.assetName, file_id: aiState?.image?.id.toString() }
    await callApi(payload)
  }, [aiState, callApi])

  const onTryAgain = useCallback(() => {
    setResponseData(undefined)
    setAIState({ assetName, image })
    setHasSubmitted(false)
  }, [assetName, image])

  return {
    aiState,
    setAIState,
    responseData,
    hasSubmitted,
    onSubmit,
    onTryAgain,
    isLoading,
  }
}
